/**
 * Reveal on scroll. Replaces AOS 2.1.1 -- 25.9 KB of CSS and 12.4 KB of JS on
 * every page, for two animations.
 *
 * The site used `fade` and `fade-up`, at three durations and one offset.
 * Counted across every rendered page, not guessed. AOS ships fourteen
 * animations, flip and zoom families, anchor placements and a 3000-step
 * duration table; none of it had a consumer here.
 *
 * Opt in from markup: `data-reveal="fade"` or `data-reveal="up"`, with
 * `data-reveal-duration` in milliseconds and `data-reveal-offset` in pixels.
 * The attributes were `data-aos*` and live in the ContentBlocks layouts and
 * field templates, so they were renamed by migration rather than aliased here.
 *
 * AOS listened to scroll and measured every element against the viewport on
 * each event. An IntersectionObserver is told once and reports when it matters.
 *
 * `prefers-reduced-motion` was not honoured before: everything animated. The
 * elements are simply shown, which is what the setting asks for -- the
 * stylesheet drops the transition in the same media query, so nothing slides.
 *
 * THE STAGGER, E47 § 3.4. `data-reveal-stagger` on a CONTAINER makes its
 * children arrive one after another instead of together. Three things about it
 * were measured rather than assumed, and each one killed a simpler design:
 *
 *   A per-index delay in this module would have done nothing to any of the
 *   three grids in the brief. /sortiment/, /art-of-cakes/ and /hochzeitstorten
 *   carry TWO `[data-reveal]` elements each -- the page wrapper and one
 *   ContentBlocks layout -- and never one on a grid item. The items had to
 *   become reveal targets first, which components/_reveal.scss does from the
 *   group's own selector so that nothing is added to 06_tortensorteTpl,
 *   08_galleryImageTpl or galleryTileTpl.
 *
 *   A DOM-sibling index would not have staggered the row Ben complained about
 *   either: the three flavour tiles on the home page each sit in their own
 *   `div.col-lg-4`, so every one of them is an only child and would take index
 *   0. The group is declared, and a member is resolved through the column that
 *   wraps it.
 *
 *   The order is PAINTED position, not DOM order. It is the only correct one
 *   for a CSS grid and the only possible one for the masonry gallery, where
 *   the two diverge by design. Sorted per batch, from a fresh rect: at start-up
 *   the masonry grid has not laid itself out yet, so an order computed once and
 *   kept would be the order of an unstyled column.
 */

const SELECTOR = '[data-reveal]'
const GROUP = '[data-reveal-stagger]'
const REVEALED = 'is-revealed'

/* Two steps, not one. base/_tokens.scss carries the numbers and the argument;
   the module only picks which token a group reads. */
const STEP = {
  sweep: '--motion-stagger-sweep',
  block: '--motion-stagger-block',
}

/* Two members are on the same row unless their tops differ by more than this.
   A CSS grid aligns its items exactly, but a masonry column does not, and the
   reference this is read from uses the same 4px. */
const ROW_TOLERANCE = 4

/* How far a group's count may be carried into the next batch. Without a carry,
   a tall grid scrolled into gradually restarts its wave at zero half way down
   a column; without a cap, the twentieth card of a grid entered in four goes
   waits most of a second. The batch is bounded by the viewport on its own, so
   the carry-in is the half that needs limiting. */
const CARRY_CAP = 10

/* ONE READING OF THE GROUP'S KIND. `unitsOf` and `tokenFor` each used to test
   the attribute themselves, which is one design decision written twice. */
const isSweep = (group) => group.dataset.revealStagger === 'sweep'

/* A MEMBER IS A CHILD OF THE GROUP, AND IT CAN CARRY MORE THAN ONE TARGET.
   `at` is what is watched and what supplies the painted position; `targets`
   is what actually animates.

   The child itself where the child is a reveal target or the group is a
   sweep -- components/_reveal.scss makes a sweep group's children targets
   from the group's own selector -- and everything below it where a column
   wraps content.

   A CHILD WITH NOTHING BELOW IT IS NOT A MEMBER. It used to fall back to
   being its own target, on the stated grounds that this was the grid case;
   the grid case is the sweep branch above and never reaches the fallback, so
   what the line actually caught was a column holding no reveal target at all.
   That reads as harmless and is not. Nothing in the stylesheet gives a bare
   `.col-lg-6` a transition, so the delay written on it can never run, and the
   seat it takes pushes every real member behind it one step further back.
   Measured 2026-09-21: three such columns, two on the home page and one on
   /philosophie/, one of them carrying
   `transition-delay: calc(1 * var(--motion-stagger-block))` on an element
   with no transition at all.

   ALL of a column's targets and not the first, which is the correction
   /preise forced: each of its nine columns holds a .cb-image AND a .rich-text,
   both `data-reveal="fade"`. Taking the nearest one would have staggered the
   photographs and left every caption to fade in on its own schedule -- a
   column arriving in two pieces, which is not what the column is. */
function unitsOf (group) {
  /* A SWEEP GROUP'S CHILD IS ALWAYS ITS OWN TARGET. The stylesheet hides it
     from the group's selector, so a child that handed its turn to a
     descendant would sit at opacity 0 for good -- and today it is one
     ContentBlocks field inside a Sorte card away from happening. Checked
     2026-09-21: 0 of 85 grid items carry a [data-reveal] descendant, which is
     the state this rule keeps from mattering. */
  const sweep = isSweep(group)
  const units = []
  for (const child of group.children) {
    const targets = (sweep || child.matches(SELECTOR))
      ? [child]
      : Array.from(child.querySelectorAll(SELECTOR))
    if (targets.length === 0) continue
    units.push({ at: child, targets })
  }
  return units
}

/* Row-major, against rects read once. Sorting on a live
   getBoundingClientRect() asks the browser for layout O(n log n) times for one
   answer that cannot change while the comparator runs. */
function sortPainted (units) {
  const box = new Map(units.map((unit) => [unit, unit.at.getBoundingClientRect()]))
  return units.sort((a, b) => {
    const ra = box.get(a)
    const rb = box.get(b)
    return Math.abs(ra.top - rb.top) > ROW_TOLERANCE ? ra.top - rb.top : ra.left - rb.left
  })
}

const tokenFor = (group) => STEP[isSweep(group) ? 'sweep' : 'block']

/* A SEAT BECOMES A DELAY IN ONE PLACE. Both paths that start a wave -- the
   observer and replayStagger -- carried this same line, so the shape of a
   delay lived twice in one file.

   Nothing inline on the member that starts the wave. It is the one element
   whose delay is zero either way, and an element carrying
   `transition-delay: calc(0 * …)` in its style attribute invites the next
   reader to wonder what it is waiting for. */
function show (target, seat, token) {
  if (seat > 0) target.style.transitionDelay = `calc(${seat} * var(${token}))`
  target.classList.add(REVEALED)
}

/**
 * Every element inside `root` that this module animates.
 *
 * The declared targets, plus a sweep group's children -- which
 * components/_reveal.scss makes targets from the group's own selector rather
 * than from an attribute on each item, so `[data-reveal]` alone does not find
 * them.
 *
 * Exported because src/js/filter.js settles a whole section for a deep link.
 * It kept its own selector for that and the two had already drifted: it said
 * `[data-reveal-stagger] > *`, which also claims the columns of a BLOCK group,
 * and those are not targets -- their contents are.
 */
export function revealTargetsWithin (root) {
  return root.querySelectorAll(`${SELECTOR}, [data-reveal-stagger="sweep"] > *`)
}

/**
 * Put these elements where they belong without letting them travel.
 *
 * For a reader who was PUT somewhere rather than one who scrolled there --
 * src/js/filter.js calls it for a deep link, and says why at the call site.
 *
 * THE `transition-property` LONGHAND, NOT THE `transition` SHORTHAND, and the
 * comment this replaces asserted the opposite. initReveal writes
 * `transitionDuration` inline from `data-reveal-duration`; the shorthand
 * overwrites that longhand and clearing the shorthand then takes it away for
 * good. Measured 2026-09-21 on /sortiment/#schokoladig, where the section's
 * own .rich-text lost the 500ms it asks for and fell back to the stylesheet's
 * 750.
 */
export function settleReveal (nodes) {
  for (const node of nodes) {
    node.style.transitionProperty = 'none'
    node.style.transitionDelay = ''
    node.classList.add(REVEALED)
  }
  /* Read a layout property to flush the style change before measuring. */
  void document.body.offsetHeight
  /* Hand the elements back to the stylesheet once they are in place, so a
     later transition on them is not silently dead. */
  requestAnimationFrame(() => {
    for (const node of nodes) node.style.transitionProperty = ''
  })
}

/**
 * Run a group's wave again, from zero, over whatever is visible NOW.
 *
 * src/js/filter.js calls this when the reader presses a flavour: the grid
 * reflows around a different eleven cards, and a set of cards that has never
 * been in this arrangement before has not "already been revealed" in any sense
 * the reader would recognise. A filter is a request to look at something else.
 *
 * Deliberately NOT called for a deep link. Someone who followed
 * /sortiment/#schokoladig is being PUT there, and a section that animates
 * after they arrive animates something they did not scroll to -- filter.js
 * settles that case instead, and says so.
 */
export function replayStagger (group) {
  if (!group) return
  const units = unitsOf(group).filter((unit) => !unit.at.hidden)
  const targets = units.flatMap((unit) => unit.targets)

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    targets.forEach((target) => {
      target.style.transitionDelay = ''
      target.classList.add(REVEALED)
    })
    return
  }

  /* PUT THEM BACK TO THE START WITH THE TRANSITION SWITCHED OFF, AND ONLY
     THEN LET A FRAME PASS. Taking the class off and putting it back inside one
     task does not restart anything: the browser compares the state before the
     task with the state after it, sees `is-revealed` both times, and animates
     nothing -- a forced layout in between does not change that, which is what
     made this look unreliable rather than broken. Measured 2026-09-21 on a
     sequence of filter clicks: 1 of 8 cards animated, then 5 of 5, then 0 of
     8. The ones that moved were the ones the filter had just UNHIDDEN, which
     get a fresh start state from `display` alone; every card that was already
     on screen sat still.

     So: strip with `transition-property: none`, flush that, hand the
     transition back, and add the class in the NEXT frame. One frame of a grid
     at opacity 0, which is 16ms nobody sees, in exchange for a restart that is
     the same in every engine. The longhand rather than the shorthand, for the
     reason settleReveal above gives. */
  targets.forEach((target) => {
    target.style.transitionProperty = 'none'
    target.style.transitionDelay = ''
    target.classList.remove(REVEALED)
  })
  void document.body.offsetHeight
  targets.forEach((target) => { target.style.transitionProperty = '' })

  /* Capped at the same CARRY_CAP the observer uses, for the same reason: a
     replay covers the WHOLE grid, not a viewport's worth, so an uncapped index
     put the twenty-fourth card of "Alle" a full second behind the first. Past
     the cap the rest arrive together, and all of them are below the fold by
     then anyway. */
  const token = tokenFor(group)
  requestAnimationFrame(() => {
    sortPainted(units).forEach((unit, seat) => {
      const index = Math.min(seat, CARRY_CAP)
      unit.targets.forEach((target) => show(target, index, token))
    })
  })
}

export function initReveal (root = document) {
  const groups = Array.from(root.querySelectorAll(GROUP))
  const elements = Array.from(root.querySelectorAll(SELECTOR))
  if (elements.length === 0 && groups.length === 0) return

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    elements.forEach((element) => element.classList.add(REVEALED))
    /* A sweep group's children are hidden by the stylesheet rather than by an
       attribute, so they are not in the list above -- and a reader who asked
       for less motion would have been left looking at an empty grid. Resolved
       through unitsOf rather than by walking children, so that "what a group
       speaks for" has one definition in this file and not two. */
    groups.forEach((group) => {
      for (const unit of unitsOf(group)) {
        unit.targets.forEach((target) => target.classList.add(REVEALED))
      }
    })
    return
  }

  /* One unit per member, keyed by the element the observer watches. `claimed`
     is every target a group speaks for, so the loop over loose [data-reveal]
     elements below does not observe a column's contents a second time and
     reveal them without their delay. */
  const unitOf = new Map()
  const claimed = new Set()
  const waves = new Map()
  for (const group of groups) {
    waves.set(group, { token: tokenFor(group), next: 0 })
    for (const unit of unitsOf(group)) {
      unit.group = group
      unitOf.set(unit.at, unit)
      unit.targets.forEach((target) => claimed.add(target))
    }
  }

  const reveal = (entries, observer) => {
    const batches = new Map()
    for (const entry of entries) {
      if (!entry.isIntersecting) continue
      /* AOS was initialised with once: true. Nothing here re-hides an
         element, so an observer that kept watching would only cost work. */
      observer.unobserve(entry.target)
      const unit = unitOf.get(entry.target)
      if (!unit) {
        if (!entry.target.classList.contains(REVEALED)) entry.target.classList.add(REVEALED)
        continue
      }
      /* src/js/filter.js settles a whole section at once for a deep link. A
         unit it has already dealt with must not be handed a delay after the
         fact. */
      if (unit.targets.every((target) => target.classList.contains(REVEALED))) continue
      if (!batches.has(unit.group)) batches.set(unit.group, [])
      batches.get(unit.group).push(unit)
    }

    for (const [group, units] of batches) {
      const wave = waves.get(group)

      /* WHAT THE READER HAS ALREADY GONE PAST IS NOT A WAVE. The observer's
         rootMargin extends the root 100000px upwards on purpose -- without it
         a flick, a deep link or an End key leaves every skipped element at
         opacity 0 -- so a single batch can contain a whole grid the reader
         scrolled past. Measured on /hochzeitstorten before this split: one
         scrollTo(bottom) produced one batch of 55 and a queue 2.4 seconds
         long. An element above the viewport is simply shown. */
      const arriving = []
      for (const unit of units) {
        if (unit.at.getBoundingClientRect().bottom < 0) unit.targets.forEach((t) => t.classList.add(REVEALED))
        else arriving.push(unit)
      }
      if (arriving.length === 0) continue

      const carry = Math.min(wave.next, CARRY_CAP)
      sortPainted(arriving).forEach((unit, seat) => {
        unit.targets.forEach((target) => show(target, carry + seat, wave.token))
      })
      wave.next = carry + arriving.length
    }
  }

  /* AOS's offset is per element, an IntersectionObserver's rootMargin is per
     observer -- so one observer per distinct offset. In practice that is two:
     the default and the 250px the five Trennbild sections ask for. */
  const observers = new Map()
  const observerFor = (offset) => {
    if (!observers.has(offset)) {
      observers.set(offset, new IntersectionObserver(reveal, {
        /* The top margin is deliberately enormous. An IntersectionObserver
           reports what is ON SCREEN; AOS re-measured every element against the
           trigger line on every scroll event, so anything the viewport had
           already passed counted as revealed even if the reader never saw it
           cross. Without this, jumping -- End, a deep link, a fast flick, or a
           scripted scroll -- leaves every skipped element at opacity 0. It
           showed up as whole pages differing by 20-30% between screenshot runs.
           Extending the root upward without limit restores AOS's rule: past
           the line, revealed. */
        rootMargin: `100000px 0px -${offset}px 0px`
      }))
    }
    return observers.get(offset)
  }

  /* Written as an inline style rather than as a table of duration classes.
     AOS shipped 60 of those rules to cover 50-3000ms; this accepts any value
     the content asks for and adds nothing to the stylesheet. A duration
     belongs to the element whether a group speaks for it or not. */
  elements.forEach((element) => {
    const duration = element.dataset.revealDuration
    if (duration) element.style.transitionDuration = `${duration}ms`
  })

  /* A unit is watched at its member; everything a group has not claimed is
     watched on its own, at its own offset. */
  for (const element of elements) {
    if (!claimed.has(element)) observerFor(parseInt(element.dataset.revealOffset, 10) || 0).observe(element)
  }
  for (const at of unitOf.keys()) observerFor(0).observe(at)

  /* Swup replaces the containers these elements live in, and an observer that
     is still watching them keeps every one of them alive along with the page
     they came from. Disconnecting is the whole teardown -- nothing else here
     outlives the markup. */
  return () => observers.forEach((observer) => observer.disconnect())
}
